import { useCallback, useMemo } from 'react';
import useWorkbook from 'components/ScalarSpreadsheet/utilities/useWorkbook';
import WorkboardSpreadsheetView from 'dashboard409a/components/WorkboardSpreadsheetView';
import useWorkboardModelsSheet from 'dashboard409a/hooks/useWorkboardModelsSheet';
import { MODELS_COLUMN_IDS } from 'dashboard409a/pages/Workboard/common/constants';
import getWorkboardClickedRecordId from 'dashboard409a/utilities/getWorkboardClickedRecordId';
import { WorkboardModelsSpreadsheetProps } from './WorkboardModelsSpreadsheet.props';

const INTERACTIVE_COLUMN_IDS = new Set<string>([
  MODELS_COLUMN_IDS.MODEL_STATUS,
  MODELS_COLUMN_IDS.NOTES,
]);

const WorkboardModelsSpreadsheet = ({
  models,
  visibleColumnIds,
  isLoading = false,
  isNoteLoading = false,
  columnWidths,
  sortCriteria,
  onRequestSort,
  onColumnResize,
  onRowClick,
  onStatusUpdate,
  onNoteSave,
}: WorkboardModelsSpreadsheetProps) => {
  const modelById = useMemo(
    () => new Map(models.map(model => [String(model.id), model])),
    [models]
  );

  const handleSheetClick = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const recordId = getWorkboardClickedRecordId(event, {
        interactiveColumnIds: INTERACTIVE_COLUMN_IDS,
      });
      if (!recordId) {
        return;
      }

      const model = modelById.get(recordId);
      if (!model) {
        return;
      }

      onRowClick(model, event);
    },
    [onRowClick, modelById]
  );

  const { spreadsheet } = useWorkboardModelsSheet({
    models,
    visibleColumnIds,
    isNoteLoading,
    sortCriteria,
    onRequestSort,
    onStatusUpdate,
    onNoteSave,
  });

  const spreadsheets = useMemo(() => [spreadsheet], [spreadsheet]);

  const { cells, data, onChange, workbook } = useWorkbook(spreadsheets);

  const sheet = data?.workboard_models;
  const isReady = !isLoading && Boolean(data && cells && sheet);

  return (
    <WorkboardSpreadsheetView
      visibleColumnIds={visibleColumnIds}
      columnWidths={columnWidths}
      isReady={isReady}
      sheet={sheet}
      cells={cells?.workboard_models}
      workbook={workbook}
      onChange={onChange}
      onColumnResize={onColumnResize}
      onSheetClick={handleSheetClick}
      onRequestSort={onRequestSort}
    />
  );
};

export default WorkboardModelsSpreadsheet;
